import React, {useEffect, useMemo, useState} from 'react';
import styles from "./RankingAdministration.module.scss";
import { DataGrid } from '@mui/x-data-grid';
import LoadingSpinner from "../../Components/LoadingSpinner/LoadingSpinner";
import TextInput from "../../Components/TextInput/TextInput";
import Button, {BUTTON_TYPE_SECONDARY} from "../../Components/Button/Button";
import {PROD_API_URL} from "../../constants/constants";
import TabSelector from "../../Components/TabSelector/TabSelector";
import moment from "moment";
import {handleImageUpload, hasValue} from "../../helpers/helperFunctions";
import toast from "react-hot-toast";
import {errorToast, successToast} from "../../helpers/toastCSS";
import ImageSubmit from "../../Components/ImageSubmit/ImageSubmit";
import RankingLeaderboard from "./RankingLeaderboard";
import {MenuItem, TextField} from "@mui/material";

const TAB_SETTINGS_OVERVIEW = 'Settings Overview';
const TAB_RANKING_LIST = 'Ranking List';
const TAB_LEADER_BOARD = 'Leaderboard';
const TAB_ADD_XP = 'Add Manual XP';

const tabOptions = [
  {key: TAB_SETTINGS_OVERVIEW, label: 'Settings Overview'},
  {key: TAB_RANKING_LIST, label: 'Ranking List'},
  {key: TAB_LEADER_BOARD, label: 'Leaderboard'},
  {key: TAB_ADD_XP, label: 'Add Manual XP'}

];

const SETTING_TYPE_EXPERIENCE = 'experience';

const defaultRankList = [
  { level: 1, name: 'Cadet', experience: 0, allocationStakingMultiplier: 1 },
  { level: 2, name: 'Specialist', experience: 50, allocationStakingMultiplier: 1 },
  { level: 3, name: 'Sergeant', experience: 1500, allocationStakingMultiplier: 1.01 },
  { level: 4, name: 'Master Sergeant', experience: 3500, allocationStakingMultiplier: 1.02 },
  { level: 5, name: 'Chief Master Sergeant', experience: 7000, allocationStakingMultiplier: 1.03 },
  { level: 6, name: 'Second Lieutenant', experience: 15000, allocationStakingMultiplier: 1.04 },
  { level: 7, name: 'First Lieutenant', experience: 30000, allocationStakingMultiplier: 1.05 },
  { level: 8, name: 'Captain', experience: 60000, allocationStakingMultiplier: 1.1 },
  { level: 9, name: 'Major', experience: 120000, allocationStakingMultiplier: 1.15 },
  { level: 10, name: 'Lieutenant Colonel', experience: 250000, allocationStakingMultiplier: 1.2 },
]

const RankingAdministration = ({ provider }) => {
  const [rankSettings, setRankSettings] = useState();
  const [currentTab, setCurrentTab] = useState(TAB_SETTINGS_OVERVIEW);
  const [loading, setLoading] = useState();

  const [levelList, setLevelList] = useState();
  const [maxLevel, setMaxLevel] = useState(10);

  const [createVaultAccount, setCreateVaultAccount] = useState(0);
  const [createVaultExperience, setCreateVaultExperience] = useState(0);
  const [createVaultExperience90DaysMultiplier, setCreateVaultExperience90DaysMultiplier] = useState(0);
  const [createVaultExperience180DaysMultiplier, setCreateVaultExperience180DaysMultiplier] = useState(0);
  const [createVaultExperience360DaysMultiplier, setCreateVaultExperience360DaysMultiplier] = useState(0);
  const [createVaultExperiencePerStars, setCreateVaultExperiencePerStars] = useState(0);

  const [createIdoAccountExperience, setCreateIdoAccountExperience] = useState(0);
  const [idoBaseExperience, setIdoBaseExperience] = useState(0);
  const [idoExperiencePerStars, setIdoExperiencePerStars] = useState(0);
  const [idoExperiencePerUSDC, setIdoExperiencePerUSDC] = useState(0);
  const [idoExperienceFirstPurchaseBonus, setIdoExperienceFirstPurchaseBonus] = useState(0);

  const [createRaffleAccountExperience, setCreateRaffleAccountExperience] = useState(0);
  const [raffleBaseExperience, setRaffleBaseExperience] = useState(0);
  const [raffleExperiencePerTicket, setRaffleExperiencePerTicket] = useState(0);
  const [winningRaffleExperience, setWinningRaffleExperience] = useState(0);
  const [raffleFirstPurchaseBonus, setRaffleFirstPurchaseBonus] = useState(0);

  const [dailyLoginExperience, setDailyLoginExperience] = useState(0);
  const [dailyLoginStreakBonus, setDailyLoginStreakBonus] = useState(0);
  const [completedKYCExperience, setCompletedKYCExperience] = useState(0);
  const [respondedToTutorialExperience, setRespondedToTutorialExperience] = useState(0);

  const [projectInterestExperience, setProjectInterestExperience] = useState(0);
  const [swapExperiencePerStarsSold, setSwapExperiencePerStarsSold] = useState(0);
  


  useEffect( ()=>{
    const fetchRankSettings = async () => {
      setLoading(true);

      await fetch(PROD_API_URL + `/settings/` + SETTING_TYPE_EXPERIENCE, {
        "method": "GET",
        "headers": {"content-type": "application/json", "accept": "*"},
      }).then(res=>res.json())
        .then((response)=>{

          handleFetchedRankSettings(response.data);

        });

      setLoading(false);
    }

    fetchRankSettings();
  },[]);
  
  const handleFetchedRankSettings = (response) => {
    if (!response) {
      return;
    }

    setRankSettings(response);

    setMaxLevel(response?.maxLevel);
    setLevelList(response?.levelList);
    
    const vaultResponse = response?.vaultSettings;
    setCreateVaultAccount(vaultResponse?.createVaultAccount);
    setCreateVaultExperience(vaultResponse?.createVaultExperience);
    setCreateVaultExperience90DaysMultiplier(vaultResponse?.createVaultExperience90DaysMultiplier);
    setCreateVaultExperience180DaysMultiplier(vaultResponse?.createVaultExperience180DaysMultiplier);
    setCreateVaultExperience360DaysMultiplier(vaultResponse?.createVaultExperience360DaysMultiplier);
    setCreateVaultExperiencePerStars(vaultResponse?.createVaultExperiencePerStars);
    
    const idoResponse = response?.idoSettings;
    setCreateIdoAccountExperience(idoResponse?.createIdoAccountExperience);
    setIdoBaseExperience(idoResponse?.idoBaseExperience);
    setIdoExperiencePerStars(idoResponse?.idoExperiencePerStars);
    setIdoExperiencePerUSDC(idoResponse?.idoExperiencePerUSDC);
    setIdoExperienceFirstPurchaseBonus(idoResponse?.idoExperienceFirstPurchaseBonus);
    
    const raffleResponse = response?.raffleSettings;
    setCreateRaffleAccountExperience(raffleResponse?.createRaffleAccountExperience);
    setRaffleBaseExperience(raffleResponse?.raffleBaseExperience);
    setRaffleExperiencePerTicket(raffleResponse?.raffleExperiencePerTicket);
    setWinningRaffleExperience(raffleResponse?.winningRaffleExperience);
    setRaffleFirstPurchaseBonus(raffleResponse?.raffleFirstPurchaseBonus);

    const projectInterestResponse = response?.projectInterestSettings;
    setProjectInterestExperience(projectInterestResponse?.projectInterestExperience);

    const swapResponse = response?.swapSettings;
    setSwapExperiencePerStarsSold(swapResponse?.swapExperiencePerStarsSold);

    const dailyLoginResponse = response?.dailyLoginSettings;
    setDailyLoginExperience(dailyLoginResponse?.dailyLoginExperience);
    setDailyLoginStreakBonus(dailyLoginResponse?.dailyLoginStreakBonus);

    const otherResponse = response?.otherSettings;
    setCompletedKYCExperience(otherResponse?.completedKYCExperience);
    setRespondedToTutorialExperience(otherResponse?.respondedToTutorialExperience);


  }

  const saveRankSettings = async () => {
    try {
      const body = {
        type: SETTING_TYPE_EXPERIENCE,
        maxLevel,
        levelList: levelList,
        vaultSettings: {
          createVaultAccount,
          createVaultExperience,
          createVaultExperience90DaysMultiplier,
          createVaultExperience180DaysMultiplier,
          createVaultExperience360DaysMultiplier,
          createVaultExperiencePerStars,
        },
        idoSettings: {
          createIdoAccountExperience,
          idoBaseExperience,
          idoExperiencePerStars,
          idoExperiencePerUSDC,
          idoExperienceFirstPurchaseBonus,
        },
        raffleSettings: {
          createRaffleAccountExperience,
          raffleBaseExperience,
          raffleExperiencePerTicket,
          winningRaffleExperience,
          raffleFirstPurchaseBonus,
        },
        projectInterestSettings: {
          projectInterestExperience,
        },
        swapSettings: {
          swapExperiencePerStarsSold,
        },
        dailyLoginSettings: {
          dailyLoginExperience,
          dailyLoginStreakBonus,
        },
        otherSettings: {
          completedKYCExperience,
          respondedToTutorialExperience,
        },
      };

      await fetch(PROD_API_URL + `/experienceSetting/createOrEditExperienceSetting`, {
        "method": "POST",
        "headers": {"content-type": "application/json", "accept": "*"},
        "body": JSON.stringify(body)
      });
      toast('Rank Settings Saved!', successToast);
    } catch (e) {
      console.error(e);
      toast('Error saving rank settings: ' + e, errorToast);
    }
  }

  console.log(levelList);
  return (
    <main id="home" key="home" className={styles.mainIdoContainer}>
      <div className={styles.idoAdminContainer}>
        <div className={styles.rankingHeader}>
          <h4>{currentTab}</h4>
          <TabSelector
              options={tabOptions}
              setValue={setCurrentTab}
              tabContainerClassName={styles.tabSelector}
              value={currentTab}/>
        </div>
        {currentTab === TAB_ADD_XP && <AddManualXp />}
        {currentTab === TAB_RANKING_LIST && <RankingList levelList={levelList} setLevelList={setLevelList} />}
        {currentTab === TAB_LEADER_BOARD && <RankingLeaderboard />}
        {currentTab === TAB_SETTINGS_OVERVIEW && <div className={styles.settingsContainer}>
          <h4 className={styles.settingsHeader}>Vault Settings</h4>
          <div className={styles.settingsRow}>
            <TextInput value={createVaultAccount} label="Create Vault Account Experience"
                       setValue={setCreateVaultAccount}/>
            <TextInput value={createVaultExperience90DaysMultiplier} label="90 Days Multiplier"
                       setValue={setCreateVaultExperience90DaysMultiplier}/>
            <TextInput value={createVaultExperience180DaysMultiplier} label="180 Days Multiplier"
                       setValue={setCreateVaultExperience180DaysMultiplier}/>
            <TextInput value={createVaultExperience360DaysMultiplier} label="360 Days Multiplier"
                       setValue={setCreateVaultExperience360DaysMultiplier}/>
            <TextInput value={createVaultExperiencePerStars} label="Experience Per Stars"
                       setValue={setCreateVaultExperiencePerStars}/>
          </div>
          <h4 className={styles.settingsHeader}>IDO Settings</h4>
          <div className={styles.settingsRow}>
            <TextInput value={createIdoAccountExperience} label="Create IDO Account Experience"
                       setValue={setCreateIdoAccountExperience}/>
            <TextInput value={idoBaseExperience} label="Base Experience"
                       setValue={setIdoBaseExperience}/>
            <TextInput value={idoExperiencePerStars} label="Experience Per Stars"
                       setValue={setIdoExperiencePerStars}/>
            <TextInput value={idoExperiencePerUSDC} label="Experience Per USDC"
                       setValue={setIdoExperiencePerUSDC}/>
            <TextInput value={idoExperienceFirstPurchaseBonus} label="First Purchase Bonus"
                       setValue={setIdoExperienceFirstPurchaseBonus}/>
          </div>
          <h4 className={styles.settingsHeader}>Raffle Settings</h4>
          <div className={styles.settingsRow}>
            <TextInput value={createRaffleAccountExperience} label="Create Raffle Account Experience"
                       setValue={setCreateRaffleAccountExperience}/>
            <TextInput value={raffleBaseExperience} label="Base Experience"
                       setValue={setRaffleBaseExperience}/>
            <TextInput value={raffleExperiencePerTicket} label="Experience Per Ticket"
                       setValue={setRaffleExperiencePerTicket}/>
            <TextInput value={winningRaffleExperience} label="Winning Bonus"
                       setValue={setWinningRaffleExperience}/>
            <TextInput value={raffleFirstPurchaseBonus} label="First Purchase Bonus"
                       setValue={setRaffleFirstPurchaseBonus}/>
          </div>
          <h4 className={styles.settingsHeader}>Other</h4>
          <div className={styles.settingsRow}>
            <TextInput value={projectInterestExperience} label="Submit Project Interest Bonus"
                       setValue={setProjectInterestExperience}/>
            <TextInput value={dailyLoginExperience} label="Daily Login Experience"
                       setValue={setDailyLoginExperience}/>
            <TextInput value={dailyLoginStreakBonus} label="Daily Login Streak Bonus"
                       setValue={setDailyLoginStreakBonus}/>
            <TextInput value={completedKYCExperience} label="Completed KYC Experience"
                       setValue={setCompletedKYCExperience}/>
            <TextInput value={respondedToTutorialExperience} label="Responded to Tutorial Experience"
                       setValue={setRespondedToTutorialExperience}/>
          </div>
        </div>}
        {currentTab === TAB_SETTINGS_OVERVIEW && <div className={styles.buttonContainer}>
          <Button text="Save" buttonType={BUTTON_TYPE_SECONDARY} onClick={saveRankSettings}/>
        </div>}
      </div>
    </main>

  );
}

export default RankingAdministration;

const RankingColums = [
  {field: 'level', headerName: 'Level', width: 100},
  {field: 'name', headerName: 'Name', flex: 1},
  {
    field: 'rankImage',
    headerName: 'Image',
    width: 200,
    renderCell: (params) => (
        params.value ? <img className={styles.rankImage} src={params.value} alt="profileImage"/> : ''
    )
  },
  {field: 'experience', headerName: 'Experience Required', width: 200},
  {field: 'allocationStakingMultiplier', headerName: 'Alloc. Staking Multiplier', width: 250},
  {field: 'updatedAt', headerName: 'Last Updated', width: 200},
];

const RankingList = (props) => {
  const {
    levelList,
    setLevelList,
  } = props;

  const [loading, setLoading] = useState();
  const [selectedData, setSelectedData] = useState();

  const formatedRows = useMemo(() => {
    return levelList.map(row => {
      return {
        id: row.level,
        ...row,
        updatedAt: hasValue(row.updatedAt) ? moment.unix(row.updatedAt).format('YYYY-MM-DD , h:mm A') : '',
      }
    })
  }, [levelList]);

  const updateLevelList = async () => {
    const index = levelList.findIndex(row => row.level === selectedData.level);
    const newList = [...levelList];

    let updatedData;
    if (hasValue(selectedData.updatedAt)) {
      updatedData = selectedData;
      updatedData.updatedAt = moment().unix();
    } else {
      updatedData = {
        ...selectedData,
        updatedAt: moment().unix(),
      }
    }

    if (updatedData.rankImage && updatedData.rankImage !== levelList[index].rankImage) {
      updatedData.rankImage = await handleImageUpload(updatedData.rankImage, 'ranks', updatedData.name);
    }

    newList[index] = updatedData;
    setLevelList(newList);
    setSelectedData(undefined);
  }


  return (
    <div className={styles.stakerContainer}>
      {loading && <LoadingSpinner local/>}
        <div className={styles.tableContainer}>
        {formatedRows &&
        <DataGrid
            rows={formatedRows}
            onCellDoubleClick={(params) => setSelectedData(params.row)}
            columns={RankingColums}
        />}
      </div>
      <div className={styles.buttonContainer}>
        <Button text="Add Level" buttonType={BUTTON_TYPE_SECONDARY}/>
      </div>
      {selectedData && <div className={styles.rankingModalContainer}>
        <div className={styles.rankingModalContent}>
          <h4>Edit Rank</h4>
          <TextInput value={selectedData?.level} label="Level"
                     setValue={(value) => setSelectedData(prev => ({...prev, level: value}))}/>
          <TextInput value={selectedData?.name} label="Name"
                     setValue={(value) => setSelectedData(prev => ({...prev, name: value}))}/>
          <ImageSubmit preview={selectedData?.rankImage} setFileInfo={(value) => setSelectedData(prev => ({...prev, rankImage: value}))} text="Rank Image"/>
          <TextInput value={selectedData?.experience} label="Experience"
                     setValue={(value) => setSelectedData(prev => ({...prev, experience: value}))}/>
          <TextInput value={selectedData?.allocationStakingMultiplier} label="Allocation Staking Multiplier"
                     setValue={(value) => setSelectedData(prev => ({...prev, allocationStakingMultiplier: value}))}/>
          <div className={styles.buttonContainer}>
            <Button text="Cancel" onClick={() => setSelectedData(undefined)} buttonType={BUTTON_TYPE_SECONDARY}/>
            <Button text="Save" buttonType={BUTTON_TYPE_SECONDARY} onClick={updateLevelList} />
          </div>
        </div>
      </div>}
    </div>
  )
}

const typeOptions = [
  {
    value: 'vault',
    label: 'Vault',
  },
  {
    value: 'ido',
    label: 'IDO',
  },
  {
    value: 'raffle',
    label: 'Raffle',
  },
  {
    value: 'projectInterest',
    label: 'Project Interest',
  },
  {
    value: 'swap',
    label: 'Swap',
  },
  {
    value: 'tutorial',
    label: 'Tutorial',
  },
  {
    value: 'other',
    label: 'Other',
  },
];

const AddManualXp = () => {
  const [walletAddress, setWalletAddress] = useState();
  const [xpAmount, setXpAmount] = useState();
  const [label, setLabel] = useState();
  const [type, setType] = useState();
  const [action, setAction] = useState();

  const submitXP = async () => {
    try {
      await fetch(PROD_API_URL + `/experience/addXPHistory`, {
        "method": "POST",
        "headers": {"content-type": "application/json", "accept": "*"},
        "body": JSON.stringify({
          solanaWallet: walletAddress,
          experiencePoints: xpAmount,
          labelArray: [label],
          type,
          action: action ? action : 'manual',
        })
      });
      toast('XP Added!', successToast);
    } catch (e) {
      console.error(e);
      toast('Error adding XP: ' + e, errorToast);
    }
  }

  return (
      <div className={styles.addManualXpContainer}>
        <TextInput value={walletAddress} label="Wallet Address" setValue={setWalletAddress}/>
        <TextInput value={xpAmount} label="XP Amount" setValue={setXpAmount}/>
        <TextInput value={label} label="Label" setValue={setLabel}/>
        <div className={styles.checkboxContainer}>
          <TextField
              id="outlined-select-currency"
              select
              label="Type"
              defaultValue={type}
              helperText="Select Type">
            {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} onClick={() => setType(option.value)}>
                  {option.label}
                </MenuItem>
            ))}
          </TextField>
        </div>
        <Button text="Add XP" buttonType={BUTTON_TYPE_SECONDARY} onClick={submitXP}/>
      </div>
  )
}